import camelcaseKeys from "camelcase-keys";
import moment from "moment-timezone";
import { nanoid } from "nanoid";

import * as collaborationApi from "@/api/collaboration";
import { getMentionUserIdsFromNames } from "@/components/Collaboration/CollaborationEditor/mention-helpers";
import { SOCKET_URL } from "@/env";
import { createAlertAction } from "@/redux/alerts/alerts.actions";
import orders from "@/redux/modules/orders";
import { createPurchaseOrderAction } from "@/redux/purchaseOrders/purchaseOrders.actions";
import { handleError } from "@/redux/utils/error";
import * as date from "@/utils/date";
import { parseApiError } from "@/utils/parseApiError";
import WebSocketManager, {
  CLOSE_CODES,
  getBackoffDelay,
  isRetryableClose,
} from "@/utils/websocket";

import {
  ADD_ATTACHMENT_FILE,
  APPEND_ATTACHMENTS_TO_UPLOAD,
  REMOVE_ATTACHMENT_FILE,
  RESET,
  SET_ALL_COMPANY_USERS,
  SET_ALL_MESSAGES,
  SET_ALL_NOTIFICATIONS,
  SET_ATTACHED_FILES_EDITOR,
  SET_ATTACHMENT_FILES,
  SET_ATTACHMENTS_TO_UPLOAD,
  SET_IS_LOADING,
  SET_IS_PENDING,
  SET_IS_UPLOAD_FILE,
  SET_REFERENCE,
  SET_SOCKET_INSTANCE,
  SET_TO_EDIT_MESSAGE,
  SET_USER_ID,
  UPDATE_MESSAGE_FILE_LABELS,
  UPDATE_MESSAGE_LABELS,
} from "./collaboration.actionTypes";
import * as selectors from "./collaboration.selectors";
import { ACTIONS, NAMESPACES, REFERENCES } from "./constants";

const webSocketManager = new WebSocketManager();
const reconnectState = new Map();

export const setSocketInstance = (namespace, payload) => (dispatch) => {
  dispatch({
    type: `${namespace}${SET_SOCKET_INSTANCE}`,
    payload,
  });
};

export const setReference = (namespace, payload) => (dispatch) => {
  dispatch({
    type: `${namespace}${SET_REFERENCE}`,
    payload,
  });
};

export const setUserId = (namespace, payload) => (dispatch) => {
  dispatch({
    type: `${namespace}${SET_USER_ID}`,
    payload,
  });
};

export const setIsLoading = (namespace, payload) => (dispatch) => {
  dispatch({
    type: `${namespace}${SET_IS_LOADING}`,
    payload,
  });
};

export const setAllMessages =
  (namespace, payload = []) =>
  (dispatch) => {
    dispatch({
      type: `${namespace}${SET_ALL_MESSAGES}`,
      payload: payload.map((message) => ({
        ...message,
        createdTimestampUnix: message.createdTimestamp
          ? moment(message.createdTimestamp).tz(date.getCurrentUserTimezone()).unix()
          : null,
      })),
    });
  };

export const setAllNotifications = (namespace, payload) => (dispatch) => {
  dispatch({
    type: `${namespace}${SET_ALL_NOTIFICATIONS}`,
    payload,
  });
};

export const setAttachedFilesEditor = (namespace, payload) => (dispatch) => {
  dispatch({
    type: `${namespace}${SET_ATTACHED_FILES_EDITOR}`,
    payload,
  });
};

export const setAllCompanyUsers = (namespace, payload) => (dispatch) => {
  dispatch({
    type: `${namespace}${SET_ALL_COMPANY_USERS}`,
    payload: payload.map((user) => ({ ...user, name: user.firstName })),
  });
};

export const setToEditMessage = (namespace, payload) => (dispatch) => {
  dispatch({
    type: `${namespace}${SET_TO_EDIT_MESSAGE}`,
    payload,
  });
};

export const updateMessageLabels = (namespace, messageId, labels) => (dispatch) => {
  dispatch({
    type: `${namespace}${UPDATE_MESSAGE_LABELS}`,
    payload: { messageId, labels },
  });
};

export const updateMessageFileLabels = (namespace, messageId, fileId, labels) => (dispatch) => {
  dispatch({
    type: `${namespace}${UPDATE_MESSAGE_FILE_LABELS}`,
    payload: { messageId, fileId, labels },
  });
};

export const setIsUploadFile = (namespace, payload) => (dispatch) => {
  dispatch({
    type: `${namespace}${SET_IS_UPLOAD_FILE}`,
    payload,
  });
};

export const setIsPending = (namespace, payload) => (dispatch) => {
  dispatch({
    type: `${namespace}${SET_IS_PENDING}`,
    payload,
  });
};

export const listAllNotifications = (namespace, payload) => (dispatch) => {
  const message = camelcaseKeys(payload, { deep: true });
  const allChatNotifications = message
    .map((item) => ({
      ...item,
      createdTimestampUnix: item.createdTimestamp
        ? moment(message.createdTimestamp).tz(date.getCurrentUserTimezone()).unix()
        : null,
    }))
    .sort((a, b) => a.createdTimestampUnix - b.createdTimestampUnix);

  dispatch(setAllNotifications(namespace, allChatNotifications));
};

export const receivingNotification = (namespace, payload) => (dispatch, getState) => {
  const state = getState();
  const allChatsNotifications = selectors.allChatsNotifications(namespace, state);

  const message = camelcaseKeys(payload, { deep: true });
  const index = allChatsNotifications.findIndex((item) => item.id === message.id);

  if (index >= 0) {
    dispatch(
      setAllNotifications(
        namespace,
        allChatsNotifications
          .map((item) => {
            if (item.id === message.id) {
              return {
                ...item,
                createdTimestampUnix: message.createdTimestamp
                  ? moment(message.createdTimestamp).tz(date.getCurrentUserTimezone()).unix()
                  : null,
              };
            }

            return item;
          })
          .sort((a, b) => a.createdTimestampUnix - b.createdTimestampUnix)
      )
    );
  } else {
    dispatch(
      setAllNotifications(
        namespace,
        [
          ...allChatsNotifications,
          {
            ...message,
            createdTimestampUnix: message.createdTimestamp
              ? moment(message.createdTimestamp).tz(date.getCurrentUserTimezone()).unix()
              : null,
          },
        ].sort((a, b) => a.createdTimestampUnix - b.createdTimestampUnix)
      )
    );
  }
};

export const readingNotifications = (namespace, payload) => (dispatch, getState) => {
  const state = getState();
  const notification = camelcaseKeys(payload, { deep: true });
  const notifications = selectors.allChatsNotifications(namespace, state);
  const index = notifications.findIndex(
    (message) => message.id === notification.notificationId?.[0]
  );

  if (index >= 0) {
    dispatch(
      setAllNotifications(
        namespace,
        notifications.map((item, key) => {
          if (key === index) {
            return {
              ...item,
              isRead: true,
            };
          }

          return item;
        })
      )
    );
  }
};

export const markAllNotificationsAsRead = (namespace) => (dispatch, getState) => {
  const state = getState();
  const allChatsNotifications = selectors.allChatsNotifications(namespace, state);

  dispatch(
    setAllNotifications(
      namespace,
      allChatsNotifications.map((item) => ({
        ...item,
        isRead: true,
      }))
    )
  );
};

export const deleteNotification = (namespace, payload) => (dispatch, getState) => {
  const state = getState();
  const notification = camelcaseKeys(payload, { deep: true });
  const allChatsNotifications = selectors.allChatsNotifications(namespace, state);

  dispatch(
    setAllNotifications(
      namespace,
      allChatsNotifications
        .filter((item) => item.id !== notification.notificationId)
        .sort((a, b) => a.createdTimestampUnix - b.createdTimestampUnix)
    )
  );
};

const validateNameSpace = (namespace, dispatch) => {
  if (!Object.values(NAMESPACES).includes(namespace)) {
    handleError({ response: { data: `Invalid namespace ${namespace}` } }, dispatch);
    return false;
  }

  return true;
};

export function addAlert(_namespace, { type, message, actionFn, actionLabel = "" }) {
  return async (dispatch) => {
    dispatch(createAlertAction(nanoid(), message, !actionFn, type, actionLabel, actionFn));
  };
}

export function fetchAllMessages(namespace, reference) {
  return async (dispatch, getState) => {
    if (!validateNameSpace(namespace, dispatch)) {
      return;
    }

    const state = getState();
    const socket = selectors.socketInstance(namespace, state);

    try {
      socket.send(
        JSON.stringify({
          type: ACTIONS.GET_ALL_MESSAGES,
          [REFERENCES[namespace]]: reference,
        })
      );
    } catch (error) {
      handleError(error, dispatch);
    }
  };
}

export const appendNewMessage = (namespace, message) => (dispatch, getState) => {
  if (!validateNameSpace(namespace, dispatch)) {
    return;
  }

  const state = getState();
  const allMessages = selectors.allMessages(namespace, state);

  const newMessageList = [
    ...(allMessages.length > 0 ? allMessages : []),
    {
      ...message,
      createdTimestampUnix: message.createdTimestamp
        ? moment(message.createdTimestamp).tz(date.getCurrentUserTimezone()).unix()
        : null,
    },
  ];

  newMessageList.sort((a, b) => a.createdTimestampUnix - b.createdTimestampUnix);

  dispatch(setAllMessages(namespace, newMessageList));
};

export function sendNewMessage(namespace, reference, { message, mentionUserId = null }) {
  return async (dispatch, getState) => {
    const state = getState();

    if (!validateNameSpace(namespace, dispatch)) {
      return;
    }

    const userId = selectors.userId(namespace, state);

    let referenceId = reference;

    if (referenceId === "new" && namespace === NAMESPACES.ORDER) {
      await dispatch(orders.actions.submitOrderDetails()).then((data) => {
        referenceId = data.order;
        dispatch(setReference(namespace, referenceId));

        dispatch(createCollaborateInstance(namespace, referenceId, { userId }));

        setTimeout(() => {
          dispatch(fetchAllMessages(namespace, referenceId));
        }, 100);
      });
    }

    if (referenceId === undefined && namespace === NAMESPACES.PURCHASE_ORDER) {
      const activePurchaseOrder = getState().getIn(["purchaseOrders", "activePurchaseOrder"]);

      // Construct the post object
      const postPurchaseOrder = {
        supplier: activePurchaseOrder.supplier,
        contact: activePurchaseOrder.contact,
        editStatus: activePurchaseOrder.editStatus.id,
        requiredData: activePurchaseOrder.requiredDate,
        notes: activePurchaseOrder.notes,
        pickupNotes: activePurchaseOrder.pickupNotes,
        receptionMethod: activePurchaseOrder.receptionMethod,
        reference: activePurchaseOrder.reference,
        items: activePurchaseOrder.items,
        itemTotal: activePurchaseOrder.itemTotal,
        deliveryFee: activePurchaseOrder.deliveryFee,
        gstAmount: activePurchaseOrder.gstAmount,
        total: activePurchaseOrder.total,
        discountRates: activePurchaseOrder.discountRates,
        billingAddress1: activePurchaseOrder.billingAddress1,
        billingAddress2: activePurchaseOrder.billingAddress2,
        billingSuburb: activePurchaseOrder.billingSuburb,
        billingState: activePurchaseOrder.billingState,
        billingPostcode: activePurchaseOrder.billingPostcode,
        deliveryAddress1: activePurchaseOrder.deliveryAddress1,
        deliveryAddress2: activePurchaseOrder.deliveryAddress2,
        deliverySuburb: activePurchaseOrder.deliverySuburb,
        deliveryState: activePurchaseOrder.deliveryState,
        deliveryPostcode: activePurchaseOrder.deliveryPostcode,
        oneTimeContactName: activePurchaseOrder.oneTimeContactName,
        oneTimeContactEmail: activePurchaseOrder.oneTimeContactEmail,
        oneTimeContactPhoneNumber: activePurchaseOrder.oneTimeContactPhoneNumber,
        oneTimeContactMobileNumber: activePurchaseOrder.oneTimeContactMobileNumber,
      };
      const data = await dispatch(createPurchaseOrderAction(postPurchaseOrder));
      referenceId = data.uid;

      dispatch(setReference(namespace, referenceId));

      dispatch(createCollaborateInstance(namespace, referenceId, { userId }));

      setTimeout(() => {
        dispatch(fetchAllMessages(namespace, referenceId));
      }, 100);
    }

    const socket = selectors.socketInstance(namespace, state);

    const data = {
      type: ACTIONS.CREATE_MESSAGE,
      message,
      [REFERENCES[namespace]]: referenceId,
    };

    if (mentionUserId) {
      data.user_id = mentionUserId;
    }

    dispatch(setIsPending(namespace, true));

    socket.send(JSON.stringify(data));
  };
}

export function sendNewMessageWithAttachedFiles(
  namespace,
  reference,
  {
    messageFiles,
    message,
    mentionUserId = null,
    editId = null,
    customerName = "",
    isSignature = false,
    attachmentsToUpload = [],
  }
) {
  return async (dispatch, getState) => {
    const state = getState();

    if (!validateNameSpace(namespace, dispatch)) {
      return;
    }

    const userId = selectors.userId(namespace, state);

    let referenceId = reference;

    if (referenceId === "new" && namespace === NAMESPACES.ORDER) {
      await dispatch(orders.actions.submitOrderDetails()).then((data) => {
        referenceId = data.order;
        dispatch(setReference(namespace, referenceId));

        dispatch(createCollaborateInstance(namespace, referenceId, { userId }));

        setTimeout(() => {
          dispatch(fetchAllMessages(namespace, referenceId));
        }, 100);
      });
    }

    if (referenceId === undefined && namespace === NAMESPACES.PURCHASE_ORDER) {
      const activePurchaseOrder = getState().getIn(["purchaseOrders", "activePurchaseOrder"]);

      // Construct the post object
      const postPurchaseOrder = {
        supplier: activePurchaseOrder.supplier,
        contact: activePurchaseOrder.contact,
        editStatus: activePurchaseOrder.editStatus.id,
        requiredData: activePurchaseOrder.requiredDate,
        notes: activePurchaseOrder.notes,
        pickupNotes: activePurchaseOrder.pickupNotes,
        receptionMethod: activePurchaseOrder.receptionMethod,
        reference: activePurchaseOrder.reference,
        items: activePurchaseOrder.items,
        itemTotal: activePurchaseOrder.itemTotal,
        deliveryFee: activePurchaseOrder.deliveryFee,
        gstAmount: activePurchaseOrder.gstAmount,
        total: activePurchaseOrder.total,
        discountRates: activePurchaseOrder.discountRates,
        billingAddress1: activePurchaseOrder.billingAddress1,
        billingAddress2: activePurchaseOrder.billingAddress2,
        billingSuburb: activePurchaseOrder.billingSuburb,
        billingState: activePurchaseOrder.billingState,
        billingPostcode: activePurchaseOrder.billingPostcode,
        deliveryAddress1: activePurchaseOrder.deliveryAddress1,
        deliveryAddress2: activePurchaseOrder.deliveryAddress2,
        deliverySuburb: activePurchaseOrder.deliverySuburb,
        deliveryState: activePurchaseOrder.deliveryState,
        deliveryPostcode: activePurchaseOrder.deliveryPostcode,
        oneTimeContactName: activePurchaseOrder.oneTimeContactName,
        oneTimeContactEmail: activePurchaseOrder.oneTimeContactEmail,
        oneTimeContactPhoneNumber: activePurchaseOrder.oneTimeContactPhoneNumber,
        oneTimeContactMobileNumber: activePurchaseOrder.oneTimeContactMobileNumber,
      };
      const data = await dispatch(createPurchaseOrderAction(postPurchaseOrder));
      referenceId = data.uid;

      dispatch(setReference(namespace, referenceId));

      dispatch(createCollaborateInstance(namespace, referenceId, { userId }));

      setTimeout(() => {
        dispatch(fetchAllMessages(namespace, referenceId));
      }, 100);
    }

    const formData = new FormData();
    const referenceKey = REFERENCES[namespace];
    const data = {
      [referenceKey]: referenceId,
      message,
    };

    if (mentionUserId) {
      data.user_id = mentionUserId;
    }

    if (editId) {
      data.message_id = editId;
    } else {
      data.customer_name = customerName;
      data.is_signature = `${isSignature}`;
    }

    if (attachmentsToUpload?.length) {
      data.attachment_ids = attachmentsToUpload;
    }

    formData.set("json", JSON.stringify(data));

    if (!attachmentsToUpload?.length) {
      Object.keys(messageFiles).forEach((key) => {
        const file = messageFiles[key];
        formData.append("files", file);
      });
    }

    dispatch(setIsPending(namespace, true));

    collaborationApi
      .saveMessageWithFiles(namespace, formData)
      .then(() => {
        dispatch(fetchAllMessages(namespace, reference));
        dispatch(setAttachedFilesEditor(namespace, []));
        dispatch(setAttachmentsToUpload(namespace, []));
      })
      .catch((error) => {
        dispatch(
          addAlert(namespace, {
            type: "danger",
            message:
              error.response.status === 413
                ? "Saving: Large size of the attached file - Server Error"
                : "Saving: Files is not saved - Server Error",
          })
        );

        return new Promise((_, reject) => {
          reject(new Error(error));
        });
      })
      .finally(() => {
        dispatch(setIsPending(namespace, false));
      });
  };
}

export function setToEditMessageData(namespace, id) {
  return async (dispatch, getState) => {
    if (!validateNameSpace(namespace, dispatch)) {
      return;
    }

    const state = getState();
    const allMessages = selectors.allMessages(namespace, state);
    const allCompanyUsers = selectors.allCompanyUsers(namespace, state);
    const usersList = new Map();
    allCompanyUsers.map((item) => usersList.set(item.id, item));

    const toEditMessage = allMessages.filter((item) => item.id === id) || [];

    if (toEditMessage.length) {
      const [editMessageRow] = toEditMessage;
      const { userId: mentionedUsersId } = editMessageRow;

      if (mentionedUsersId.length) {
        const mentionedUsersFullData = mentionedUsersId.map((item) =>
          usersList.get(item.toString())
        );
        toEditMessage[0].users = mentionedUsersFullData;
      } else {
        toEditMessage[0].users = [];
      }
    }

    dispatch(setToEditMessage(namespace, toEditMessage?.[0]));
  };
}

export function setToEditMessages(namespace, updatedMessage) {
  return async (dispatch, getState) => {
    const state = getState();
    const allMessages = selectors.allMessages(namespace, state);

    const updatedMessages = allMessages
      .map((message) => {
        if (message.id === updatedMessage.id) {
          return { ...message, ...updatedMessage };
        }

        return message;
      })
      .map((message) => ({
        ...message,
        createdTimestampUnix: message.createdTimestamp
          ? moment(message.createdTimestamp).tz(date.getCurrentUserTimezone()).unix()
          : null,
      }))
      .sort((a, b) => a.createdTimestampUnix - b.createdTimestampUnix);

    dispatch(setAllMessages(namespace, updatedMessages));
  };
}

export function setAttachmentFiles(namespace, payload) {
  return (dispatch, getState) => {
    const state = getState();
    const attachmentFiles = selectors.attachmentFiles(namespace, state);

    dispatch({
      type: `${namespace}${SET_ATTACHMENT_FILES}`,
      payload: { ...attachmentFiles, ...payload },
    });
  };
}

export function addAttachmentFile(namespace, fileId, fileEntry) {
  return (dispatch) => {
    dispatch({
      type: `${namespace}${ADD_ATTACHMENT_FILE}`,
      payload: { fileId, fileEntry },
    });
  };
}

export function removeAttachmentFile(namespace, fileId) {
  return (dispatch, getState) => {
    const existingEntry = selectors.attachmentFiles(namespace, getState())[fileId];
    if (existingEntry?.url) {
      window.URL.revokeObjectURL(existingEntry.url);
    }
    dispatch({
      type: `${namespace}${REMOVE_ATTACHMENT_FILE}`,
      payload: { fileId },
    });
  };
}

export function clearAttachmentFiles(namespace) {
  return (dispatch, getState) => {
    const existing = selectors.attachmentFiles(namespace, getState()) || {};
    Object.values(existing).forEach((entry) => {
      if (entry?.url) {
        window.URL.revokeObjectURL(entry.url);
      }
    });
    // Dispatch the SET_ATTACHMENT_FILES action directly with an empty payload —
    // the `setAttachmentFiles` thunk merges with existing state, which would
    // otherwise leave the entries we just revoked still in the cache.
    dispatch({
      type: `${namespace}${SET_ATTACHMENT_FILES}`,
      payload: {},
    });
  };
}

export function getAttachmentFiles(namespace, fileId, fileName) {
  return async (dispatch, getState) => {
    const state = getState();
    const attachmentFiles = selectors.attachmentFiles(namespace, state);

    collaborationApi.getAttachmentFiles(fileId).then((response) => {
      dispatch(
        setAttachmentFiles(namespace, {
          ...attachmentFiles,
          [fileId]: {
            id: fileId,
            url: window.URL.createObjectURL(
              new File([response.data], fileName, { type: response.data.type })
            ),
            type: response.data.type,
            name: fileName,
          },
        })
      );

      return new Blob([response.data], { type: response.data.type });
    });
  };
}

export function afterDeleteMessage(namespace, id) {
  return async (dispatch, getState) => {
    if (!validateNameSpace(namespace, dispatch)) {
      return;
    }

    const state = getState();
    const allMessages = selectors.allMessages(namespace, state);

    const deleteId = parseFloat(id);

    const newList = allMessages
      .filter((item) => item.id !== deleteId)
      .sort((a, b) => a.createdTimestampUnix - b.createdTimestampUnix);

    dispatch(setAllMessages(namespace, newList));
    dispatch(setIsPending(namespace, false));
  };
}

export function editMessage(namespace, reference, message, selectedMentionUser) {
  return async (dispatch, getState) => {
    if (!validateNameSpace(namespace, dispatch)) {
      return;
    }

    const referenceKey = REFERENCES[namespace];

    const state = getState();
    const socket = selectors.socketInstance(namespace, state);
    const { id: editId } = selectors.toEditMessage(namespace, state);
    const messageFiles = selectors.attachedFilesEditor(namespace, state);
    const allCompanyUsers = selectors.allCompanyUsers(namespace, state);
    const currentUserId = selectors.userId(namespace, state);
    const mentionUserId = getMentionUserIdsFromNames(
      Object.values(selectedMentionUser),
      allCompanyUsers,
      currentUserId
    );

    if (messageFiles && messageFiles.length > 0) {
      dispatch(
        sendNewMessageWithAttachedFiles(namespace, reference, {
          messageFiles,
          message,
          mentionUserId,
          editId,
        })
      );
    }

    const socketData = {
      type: ACTIONS.EDIT_MESSAGE,
      message,
      message_id: editId,
      [referenceKey]: reference,
    };

    if (mentionUserId) {
      socketData.user_id = mentionUserId;
    }

    dispatch(setIsPending(namespace, true));

    socket.send(JSON.stringify(socketData));

    dispatch(setToEditMessage(namespace, null));
  };
}

export function deleteMessageFile(namespace, reference, { fileId, messageId }) {
  return async (dispatch, getState) => {
    if (!validateNameSpace(namespace, dispatch)) {
      return;
    }

    const state = getState();
    const socket = selectors.socketInstance(namespace, state);
    const referenceKey = REFERENCES[namespace];
    const allMessages = selectors.allMessages(namespace, state);

    const messageIndex = allMessages.findIndex((item) => item.id === messageId);
    const newFileList = allMessages.map((message, index) => ({
      ...message,
      orderChatMessageFile:
        messageIndex === index
          ? message.orderChatMessageFile.filter((file) => file.id !== fileId)
          : message.orderChatMessageFile,
    }));

    dispatch(setAllMessages(namespace, newFileList));
    dispatch(removeAttachmentFile(namespace, fileId));

    socket.send(
      JSON.stringify({
        type: ACTIONS.DELETE_FILE_MESSAGE,
        file_id: fileId.toString(),
        [referenceKey]: reference,
      })
    );

    dispatch(
      addAlert(namespace, {
        type: "danger",
        message: "Attachment deleted",
        actionFn: () => {
          dispatch(setAllMessages(namespace, allMessages));

          socket.send(
            JSON.stringify({
              type: ACTIONS.UNDO_DELETE_FILE_MESSAGE,
              file_id: fileId.toString(),
              [referenceKey]: reference,
            })
          );
        },
        actionLabel: "Undo",
        closeDelay: 5000,
      })
    );
  };
}

export function getAllCompanyUsers(namespace) {
  return async (_, getState) => {
    const state = getState();
    const socket = selectors.socketInstance(namespace, state);

    const allCompanyUsers = selectors.allCompanyUsers(namespace, state);

    if (!allCompanyUsers?.length) {
      socket.send(
        JSON.stringify({
          type: ACTIONS.GET_ALL_COMPANY_USER,
        })
      );
    }
  };
}

export function deleteMessage(namespace, reference, messageId) {
  return async (dispatch, getState) => {
    const state = getState();
    const socket = selectors.socketInstance(namespace, state);
    const referenceKey = REFERENCES[namespace];
    const allMessages = selectors.allMessages(namespace, state);

    const newMessageList = allMessages.filter((item) => item.id !== messageId);

    dispatch(setAllMessages(namespace, newMessageList));

    socket.send(
      JSON.stringify({
        type: ACTIONS.DELETE_MESSAGE,
        message_id: messageId.toString(),
        [referenceKey]: reference,
      })
    );

    dispatch(
      addAlert(namespace, {
        type: "danger",
        message: "Message deleted",
        actionFn: () => {
          dispatch(setAllMessages(namespace, allMessages));

          socket.send(
            JSON.stringify({
              type: ACTIONS.UNDO_DELETE_MESSAGE,
              message_id: messageId.toString(),
              [referenceKey]: reference,
            })
          );
        },
        actionLabel: "Undo",
        closeDelay: 5000,
      })
    );
  };
}

export function toggleAttachment({ attachmentType, fileId }) {
  return async (dispatch) => {
    collaborationApi.toggleAttachment({ attachmentType, fileId }).catch((error) => {
      dispatch(
        addAlert(attachmentType, {
          type: "danger",
          message: parseApiError(error).message || "Oops, something went wrong",
        })
      );
    });
  };
}

export function createCollaborateInstance(namespace, reference, { userId }) {
  return async (dispatch) => {
    if (!validateNameSpace(namespace, dispatch)) {
      return;
    }

    dispatch(setIsLoading(namespace, true));

    const existing = reconnectState.get(namespace);
    if (existing?.reconnectTimer) {
      clearTimeout(existing.reconnectTimer);
    }
    reconnectState.set(namespace, { shouldReconnect: true, reconnectTimer: null });

    const scheduleReconnect = (delay) => {
      const state = reconnectState.get(namespace);
      if (!state?.shouldReconnect) {
        return;
      }
      if (state.reconnectTimer) {
        clearTimeout(state.reconnectTimer);
      }
      state.reconnectTimer = setTimeout(() => {
        const current = reconnectState.get(namespace);
        if (!current?.shouldReconnect) {
          return;
        }
        current.reconnectTimer = null;
        connect();
      }, delay);
    };

    const MAX_RETRIES = 3;
    let retryCount = 0;
    // Tracked across the whole instance lifetime (not reset on open) so a socket
    // that keeps opening then closing with AUTH_FAILED cannot reconnect forever.
    let authFailureCount = 0;

    const validateMessageReference = (messageData) => {
      if (!messageData || !reference) {
        return true;
      }

      // REFERENCES keys are snake_case, but the incoming payload is deep-camelCased
      // above, so camelcase the key with the same transform before the lookup.
      const referenceKey = Object.keys(
        camelcaseKeys({ [REFERENCES[namespace]]: reference })
      )[0];
      if (referenceKey && messageData[referenceKey]) {
        return messageData[referenceKey] === reference;
      }

      return true;
    };

    const connect = async () => {
      let onCloseFired = false;
      try {
        const socket = await webSocketManager.createWebSocket(
          namespace,
          `${SOCKET_URL}user_connect/${userId}`,
          {
            onMessage: (event) => {
              const data = camelcaseKeys(JSON.parse(event.data), { deep: true });

              switch (data.type) {
                case ACTIONS.CREATE_MESSAGE:
                  if (validateMessageReference(data)) {
                    dispatch(setIsPending(namespace, false));
                    dispatch(fetchAllMessages(namespace, reference));
                    dispatch(setIsLoading(namespace, false));
                  }
                  break;
                case ACTIONS.GET_ALL_MESSAGES:
                  if (validateMessageReference(data) && data.messages) {
                    const validMessages = data.messages.filter((msg) =>
                      validateMessageReference(msg)
                    );
                    dispatch(setAllMessages(namespace, validMessages));
                    dispatch(setIsLoading(namespace, false));
                    dispatch(getAllCompanyUsers(namespace));
                  }
                  break;
                case ACTIONS.EDIT_MESSAGE:
                  if (validateMessageReference(data)) {
                    dispatch(setToEditMessages(namespace, data.message));
                    dispatch(setIsPending(namespace, false));
                  }
                  break;
                case ACTIONS.DELETE_MESSAGE:
                  if (validateMessageReference(data)) {
                    dispatch(afterDeleteMessage(namespace, data.message));
                  }
                  break;
                case ACTIONS.GET_ALL_COMPANY_USER:
                  dispatch(setAllCompanyUsers(namespace, data.messages));
                  break;
                default:
                  break;
              }
            },
            onClose: (event) => {
              onCloseFired = true;
              dispatch(setSocketInstance(namespace, null));

              if (event.code === CLOSE_CODES.AUTH_FAILED) {
                if (authFailureCount < MAX_RETRIES) {
                  authFailureCount += 1;
                  scheduleReconnect(getBackoffDelay(authFailureCount));
                } else {
                  console.error(
                    `WebSocket [${namespace}]: Max auth retries (${MAX_RETRIES}) reached.`
                  );
                  // Terminal: no more reconnects, so clear the loading spinner.
                  dispatch(setIsLoading(namespace, false));
                }
                return;
              }

              if (!isRetryableClose(event.code)) {
                if (event.code === CLOSE_CODES.FORBIDDEN || event.code === CLOSE_CODES.NOT_FOUND) {
                  dispatch(
                    addAlert(namespace, {
                      type: "danger",
                      message: `Connection refused (code: ${event.code})`,
                    })
                  );
                }
                // Terminal: a non-retryable close is never reconnected, so clear loading.
                dispatch(setIsLoading(namespace, false));
                return;
              }

              if (retryCount < MAX_RETRIES) {
                retryCount += 1;
                const delay =
                  event.code === CLOSE_CODES.RATE_LIMITED
                    ? getBackoffDelay(retryCount, { baseDelay: 10000, maxDelay: 60000 })
                    : getBackoffDelay(retryCount);
                scheduleReconnect(delay);
              } else {
                console.error(`WebSocket [${namespace}]: Max retries (${MAX_RETRIES}) reached.`);
                // Terminal: retry budget exhausted, so clear loading.
                dispatch(setIsLoading(namespace, false));
              }
            },
            onError: () => {
              webSocketManager.closeWebSocket(namespace);
            },
          }
        );

        dispatch(setSocketInstance(namespace, socket));
        retryCount = 0;
      } catch {
        // onClose is the single source of reconnect scheduling; do not double-schedule
        // here (a pre-open close already fires onClose before this promise rejects).
        // The rejection is a response-less Error, so handleError would only rethrow it
        // into an unhandled rejection.
        //
        // But a synchronous failure (corrupt auth in localStorage, WebSocket
        // constructor throwing) rejects WITHOUT ever firing onClose, so nothing has
        // cleared the loading state set in createCollaborateInstance. Recover it here
        // so the UI does not hang on a stuck spinner.
        if (!onCloseFired) {
          dispatch(setIsLoading(namespace, false));
          dispatch(setSocketInstance(namespace, null));
        }
      }
    };

    connect();
  };
}

export function closeCollaborateInstance(namespace) {
  return async (dispatch) => {
    if (!validateNameSpace(namespace, dispatch)) {
      return;
    }

    const state = reconnectState.get(namespace);
    if (state) {
      state.shouldReconnect = false;
      if (state.reconnectTimer) {
        clearTimeout(state.reconnectTimer);
        state.reconnectTimer = null;
      }
      reconnectState.delete(namespace);
    }

    dispatch(setSocketInstance(namespace, null));
    await webSocketManager.closeWebSocket(namespace);
  };
}

export function downloadMessageFilesAsZip(namespace, _reference, { messageId, fileIds }) {
  return async (dispatch) => {
    try {
      const response = await collaborationApi.downloadFilesAsZip(messageId, fileIds);

      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `attachments-${messageId}.zip`;
      document.body.appendChild(link);
      link.style = "display: none";
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      dispatch(
        addAlert(namespace, {
          type: "success",
          message: "Download started",
        })
      );
    } catch (_error) {
      dispatch(
        addAlert(namespace, {
          type: "danger",
          message: "Failed to download files",
        })
      );
    }
  };
}

export function bulkDeleteMessageFiles(namespace, reference, { messageId, fileIds }) {
  return async (dispatch, getState) => {
    if (!validateNameSpace(namespace, dispatch)) {
      return;
    }

    const state = getState();
    const allMessages = selectors.allMessages(namespace, state);

    // Optimistic update - remove files from state
    const newMessageList = allMessages.map((message) => ({
      ...message,
      orderChatMessageFile:
        message.id === messageId
          ? message.orderChatMessageFile.filter((file) => !fileIds.includes(file.id))
          : message.orderChatMessageFile,
    }));

    dispatch(setAllMessages(namespace, newMessageList));

    try {
      await collaborationApi.bulkDeleteFiles(messageId, fileIds);

      dispatch(
        addAlert(namespace, {
          type: "danger",
          message: `${fileIds.length} attachments deleted`,
          actionFn: async () => {
            try {
              await collaborationApi.bulkRestoreFiles(messageId, fileIds);
            } catch (_error) {
              dispatch(fetchAllMessages(namespace, reference));
              return;
            }
            // Restore succeeded — fetch fresh data from server so restored
            // images arrive with thumbnail_url/full_url populated, avoiding
            // the blob-fetch race that causes stuck loading spinners.
            dispatch(fetchAllMessages(namespace, reference));
          },
          actionLabel: "Undo",
          closeDelay: 5000,
        })
      );
    } catch (_error) {
      // Rollback on failure
      dispatch(setAllMessages(namespace, allMessages));

      dispatch(
        addAlert(namespace, {
          type: "danger",
          message: "Failed to delete files",
        })
      );
    }
  };
}

export function bulkLabelMessageFiles(
  namespace,
  reference,
  { messageId, labelUid, labelName, fileIds },
  { skipRefresh = false } = {}
) {
  return async (dispatch) => {
    try {
      const response = await collaborationApi.bulkLabelFiles(messageId, labelUid, fileIds);

      if (!skipRefresh) {
        dispatch(fetchAllMessages(namespace, reference));
      }

      dispatch(
        addAlert(namespace, {
          type: "success",
          message: `Label "${labelName}" applied to ${fileIds.length} files`,
        })
      );

      return response?.data;
    } catch (error) {
      const parsed = parseApiError(error);
      const isAlreadyAttached = parsed.code === "already_attached";
      dispatch(
        addAlert(namespace, {
          type: isAlreadyAttached ? "warning" : "danger",
          message: parsed.message || "Failed to apply label",
        })
      );
      throw error;
    }
  };
}

export function bulkDetachMessageFileLabels(
  namespace,
  reference,
  { attachmentIds, fileCount, labelNames },
  { skipRefresh = false } = {}
) {
  return async (dispatch) => {
    try {
      await collaborationApi.bulkDetachLabelFiles(attachmentIds);

      if (!skipRefresh) {
        dispatch(fetchAllMessages(namespace, reference));
      }

      const count = fileCount ?? attachmentIds.length;
      const labelText = labelNames?.length
        ? `Label ${labelNames.map((n) => `"${n}"`).join(", ")} removed`
        : "Label removed";
      dispatch(
        addAlert(namespace, {
          type: "success",
          message: `${labelText} from ${count} files`,
        })
      );
    } catch (error) {
      dispatch(
        addAlert(namespace, {
          type: "danger",
          message: parseApiError(error).message || "Failed to remove label",
        })
      );
      throw error;
    }
  };
}

export function setAttachmentsToUpload(namespace, payload) {
  return (dispatch) => {
    dispatch({
      type: `${namespace}${SET_ATTACHMENTS_TO_UPLOAD}`,
      payload,
    });
  };
}

export function appendAttachmentsToUpload(namespace, payload) {
  return (dispatch) => {
    dispatch({
      type: `${namespace}${APPEND_ATTACHMENTS_TO_UPLOAD}`,
      payload,
    });
  };
}

export function reset(namespace) {
  return (dispatch) => {
    // Revoke blob URLs before wiping the cache so we don't leak object URLs
    // across collaborate-instance teardown / order switches.
    dispatch(clearAttachmentFiles(namespace));
    dispatch({ type: `${namespace}${RESET}` });
  };
}
